import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Map, BookOpen, User, LogOut, PlusCircle } from "lucide-react";
import { Wordmark, BpLogo } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const navItems = [
    { to: "/plaza", label: "The Plaza", icon: Map },
    { to: "/scripture", label: "Scripture", icon: BookOpen },
    { to: "/account", label: "Account", icon: User },
  ];

  const isActive = (to) => loc.pathname === to || loc.pathname.startsWith(to + "/");

  return (
    <div className="App bp-noise min-h-screen">
      <header className="sticky top-0 z-30 border-b border-[#E8E4DF] bg-[#FAF7F2]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to={user ? "/plaza" : "/"} data-testid="nav-home-link">
            <Wordmark />
          </Link>
          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  isActive(n.to) ? "bg-[#F36353]/12 text-[#D94A3D]" : "text-[#6B6763] hover:text-[#0F3D66]"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {user && user.tier === "curator" && (
              <Button
                size="sm"
                onClick={() => nav("/host/new")}
                data-testid="nav-create-study-btn"
                className="hidden rounded-full bg-[#748C70] text-white hover:bg-[#5f7259] sm:inline-flex"
              >
                <PlusCircle size={16} className="mr-1" /> New study
              </Button>
            )}
            {user ? (
              <button
                onClick={async () => { await logout(); nav("/"); }}
                data-testid="nav-logout-btn"
                className="grid h-9 w-9 place-items-center rounded-full border border-[#E8E4DF] bg-white text-[#6B6763] transition-colors hover:text-[#F36353]"
                title="Log out"
              >
                <LogOut size={17} />
              </button>
            ) : (
              <Button onClick={() => nav("/auth")} data-testid="nav-signin-btn" className="rounded-full bg-[#F36353] text-white hover:bg-[#D94A3D]">
                Sign in
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="pb-24 md:pb-8">{children}</main>

      <footer className="mt-4 bg-[#1E2230] pb-24 pt-10 md:pb-10">
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-4 px-4 sm:px-6 md:flex-row md:items-center md:justify-between">
          <div>
            <BpLogo variant="dark" size={22} data-testid="footer-logo" />
            <p className="mt-2 font-brand text-base font-semibold text-[#F36353]">Oh, We Pray In Public</p>
          </div>
          <p className="max-w-md text-sm text-[#AEB4C2]">
            Bible study, out in the open. Studies meet only in public places across the US — never in private homes.
          </p>
          <div className="text-xs text-[#7C8397]">
            <a href="mailto:hello@bibleplaza.app" className="text-[#AEB4C2] hover:text-white" data-testid="footer-contact">hello@bibleplaza.app</a>
            <div className="mt-1">© 2026 Bible Plaza LLC</div>
          </div>
        </div>
      </footer>

      {user && (
        <nav className="fixed bottom-0 left-0 right-0 z-30 border-t border-[#E8E4DF] bg-white/95 backdrop-blur-md md:hidden">
          <div className="flex items-stretch justify-around">
            {navItems.map((n) => {
              const Icon = n.icon;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`bottomnav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium ${
                    isActive(n.to) ? "text-[#F36353]" : "text-[#6B6763]"
                  }`}
                >
                  <Icon size={20} />
                  {n.label}
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
